<template>
  <div class="employee-list">
    <!-- 헤더 -->
    <div class="list-header">
      <div class="header-left">
        <span class="title">사원 급여 정보</span>
        <span class="count">{{ employees.length }}명</span>
      </div>
      <div class="header-right">
        <button @click="$emit('toggle-whole-version')" :class="{ active: showWholeVersion }">
          {{ showWholeVersion ? '전체상세 닫기' : '전체상세' }}
        </button>
        <button @click="$emit('open-settings')" :disabled="isConfirmed">일괄설정</button>
        <button class="save-btn" :class="{ unsaved: hasUnsavedChanges }" @click="$emit('save-all')" :disabled="isSaving || isConfirmed">
          <v-progress-circular v-if="isSaving" indeterminate size="12" width="2" />
          <span v-else>{{ hasUnsavedChanges ? '저장필요' : '저장' }}</span>
        </button>
      </div>
    </div>

    <!-- 테이블 -->
    <div class="table-wrapper">
      <table class="data-table">
        <thead>
          <tr>
            <th class="c col-no" @click="toggleSort('no')">
              No
              <span v-if="sortKey === 'no'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" @click="toggleSort('name')">
              성명
              <span v-if="sortKey === 'name'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="r sortable" @click="toggleSort('days')">
              근무일수
              <span v-if="sortKey === 'days'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="r sortable" @click="toggleSort('hours')">
              근무시간
              <span v-if="sortKey === 'hours'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="r sortable" @click="toggleSort('timePay')">
              시급
              <span v-if="sortKey === 'timePay'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="r sortable" @click="toggleSort('suga')">
              수가
              <span v-if="sortKey === 'suga'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="r sortable" @click="toggleSort('salary')">
              월급여
              <span v-if="sortKey === 'salary'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="r sortable" @click="toggleSort('netPay')">
              실수령액
              <span v-if="sortKey === 'netPay'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="r highlight sortable" @click="toggleSort('profit')">
              기관수익
              <span v-if="sortKey === 'profit'" class="sort-icon">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in sortedEmployees" :key="item.originalIndex" @click="openDetail(item.originalIndex)">
            <td class="c col-no">{{ idx + 1 }}</td>
            <td class="name-cell">
              <span class="name-chip" :style="{ background: getChipColor() }">{{ getFirstChar(item.emp.name) }}</span>
              <span class="name-text">{{ item.emp.name || '-' }}</span>
              <span v-if="isFamilyCaregiver(item.emp)" class="badge">가족</span>
            </td>
            <td class="r">{{ getWorkDays(item.originalIndex) }}일</td>
            <td class="r">
              <span :class="{ warn: getTotalSugaHours(item.emp, item.originalIndex) < 60 }">{{ getTotalSugaHours(item.emp, item.originalIndex).toFixed(1) }}시간</span>
            </td>
            <td class="r">
              <span v-if="payrollDetails[item.originalIndex]?.avgTimePay > 0">{{ formatMoney(payrollDetails[item.originalIndex]?.avgTimePay) }}원</span>
              <span v-else class="muted">미설정</span>
            </td>
            <td class="r">{{ formatMoney((item.emp.totalSugaMoney || 0) + (payrollDetails[item.originalIndex]?.severeCareAddition || 0)) }}원</td>
            <td class="r">{{ formatMoney(payrollDetails[item.originalIndex]?.totalPay || 0) }}원</td>
            <td class="r">{{ formatMoney(payrollDetails[item.originalIndex]?.netPay || 0) }}원</td>
            <td class="r highlight" :class="{ negative: getProfit(item.emp, item.originalIndex) < 0 }">{{ formatMoney(getProfit(item.emp, item.originalIndex)) }}원</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 합계 -->
    <div class="list-footer">
      <div class="total-item"><span>총 수가금액</span><span>{{ formatMoney(totalSugaMoney) }}원</span></div>
      <div class="total-item"><span>총 급여</span><span>{{ formatMoney(totalBasicPay) }}원</span></div>
      <div class="total-item primary"><span>총 기관수익</span><span>{{ formatMoney(totalProfit) }}원</span></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

interface Patient {
  patientCode: string;
  patientName: string;
  sugaCode: string;
  sugaName: string;
  sugaHours: number;
  sugaMinutesKey?: number | null;
  serviceDays: number;
}

interface Employee {
  empCode: string;
  name: string;
  birth: string;
  patients: Patient[];
  totalSugaMoney: number;
}

interface Props {
  employees: Employee[];
  payrollDetails: Record<number, any>;
  settingsData: Record<number, any>;
  services: Record<number, any[]>;
  showWholeVersion: boolean;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isConfirmed: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'open-detail', index: number): void;
  (e: 'toggle-whole-version'): void;
  (e: 'open-settings'): void;
  (e: 'save-all'): void;
}>();

// 소팅 상태
type SortKey = 'no' | 'name' | 'timePay' | 'suga' | 'salary' | 'hours' | 'days' | 'netPay' | 'profit';
const sortKey = ref<SortKey>('no');
const sortOrder = ref<'asc' | 'desc'>('asc');

const toggleSort = (key: SortKey) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

// 기관수익 계산 (총수가 - 직원지급액 - 기관부담금)
const getProfit = (emp: Employee, idx: number): number => {
  const payroll = props.payrollDetails[idx];
  if (!payroll) return 0;
  
  const totalSuga = (emp.totalSugaMoney || 0) + (payroll.severeCareAddition || 0);  // 총수가
  const totalPay = payroll.totalPay || 0;  // 직원 지급액
  const centerDeduction = payroll.totalCenterDeduction || 0;  // 기관 부담금 (4대보험, 산재, 퇴직금)
  
  return totalSuga - totalPay - centerDeduction;
};

// 정렬된 사원 목록 (원본 인덱스 유지)
const sortedEmployees = computed(() => {
  const mapped = props.employees.map((emp, idx) => ({
    emp,
    originalIndex: idx,
    timePay: props.payrollDetails[idx]?.avgTimePay || 0,
    suga: (emp.totalSugaMoney || 0) + (props.payrollDetails[idx]?.severeCareAddition || 0),
    salary: props.payrollDetails[idx]?.totalPay || 0,
    hours: getTotalSugaHours(emp, idx),
    days: getWorkDays(idx),
    netPay: props.payrollDetails[idx]?.netPay || 0,
    profit: getProfit(emp, idx)
  }));

  return mapped.sort((a, b) => {
    const order = sortOrder.value === 'asc' ? 1 : -1;
    switch (sortKey.value) {
      case 'no':
        return (a.originalIndex - b.originalIndex) * order;
      case 'name':
        return (a.emp.name || '').localeCompare(b.emp.name || '', 'ko') * order;
      case 'timePay':
        return (a.timePay - b.timePay) * order;
      case 'suga':
        return (a.suga - b.suga) * order;
      case 'salary':
        return (a.salary - b.salary) * order;
      case 'hours':
        return (a.hours - b.hours) * order;
      case 'days':
        return (a.days - b.days) * order;
      case 'netPay':
        return (a.netPay - b.netPay) * order;
      case 'profit':
        return (a.profit - b.profit) * order;
      default:
        return 0;
    }
  });
});

const isFamilyCaregiver = (emp: Employee) => emp.patients?.some(p => p.sugaName?.includes('가족'));

// StepThree.vue와 동일한 getSugaKey 로직
const getSugaKey = (patient: Patient): string => {
  const hoursKey = patient.sugaHours ? `_h${patient.sugaHours}` : '';
  if (patient.sugaMinutesKey != null) {
    return `${patient.sugaCode}_${patient.sugaMinutesKey}${hoursKey}`;
  }
  return `${patient.sugaCode}${hoursKey}`;
};

// 270분초과 수가 여부 확인
const is270MinutesOverSuga = (patient: Patient): boolean => {
  return patient.sugaName?.includes('270분초과') || false;
};

// 환자별 수가시간 가져오기 (270분초과 수가는 30분 휴게시간 적용)
// 단, 8시간 이상 시에는 휴게시간 차감 없음
const getPatientSugaHours = (patient: Patient): number => {
  const baseHours = patient.sugaHours || 0;
  
  // 270분초과 수가인 경우 30분(0.5시간) 휴게시간 차감
  // 단, 8시간 이상 시에는 휴게시간 차감하지 않음
  if (is270MinutesOverSuga(patient)) {
    if (baseHours >= 8) {
      return baseHours; // 8시간 이상 시 휴게시간 차감 없음
    }
    return Math.max(0, baseHours - 0.5);
  }
  
  return baseHours;
};

const getTotalSugaHours = (emp: Employee, idx: number) => {
  if (!emp.patients) return 0;
  const settings = props.settingsData[idx];
  let total = 0;
  emp.patients.forEach(p => {
    const sugaKey = getSugaKey(p);
    const customHours = settings?.customSugaHours?.[p.patientCode]?.[sugaKey];
    // 수동 조정된 값이 있으면 그대로 사용, 없으면 휴게시간 적용된 시간 사용
    const hours = customHours !== undefined ? customHours : getPatientSugaHours(p);
    total += hours * (p.serviceDays || 0);
  });
  return total;
};

const getWorkDays = (idx: number) => {
  const serviceList = props.services[idx];
  if (!serviceList || serviceList.length === 0) return 0;
  const uniqueDates = new Set(serviceList.map(s => s.service_date));
  return uniqueDates.size;
};

// 성명 첫 글자 추출 (성)
const getFirstChar = (name: string | undefined): string => {
  if (!name) return '?';
  return name.charAt(0);
};

// 칩 색상 (통일)
const getChipColor = (): string => '#6b7280';

const totalSugaMoney = computed(() => {
  return props.employees.reduce((sum, emp, idx) => sum + (emp.totalSugaMoney || 0) + (props.payrollDetails[idx]?.severeCareAddition || 0), 0);
});

const totalBasicPay = computed(() => {
  return Object.values(props.payrollDetails).reduce((sum: number, p: any) => sum + (p?.totalPay || 0), 0);
});

const totalProfit = computed(() => {
  return props.employees.reduce((sum, emp, idx) => sum + getProfit(emp, idx), 0);
});

const openDetail = (idx: number) => emit('open-detail', idx);

const formatMoney = (amount: number | undefined | null): string => new Intl.NumberFormat('ko-KR').format(Math.round(amount || 0));
</script>

<style lang="scss" scoped>
$border: #d4d4d4;
$bg: #f5f5f5;
$text: #333;
$text-sub: #666;
$primary: #2563eb;

.employee-list {
  background: #fff;
  border: 1px solid $border;
  max-width: 1200px;
  margin: 0 auto;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 1px solid $border;

  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;

    .title { font-size: 14px; font-weight: 600; color: $text; }
    .count { font-size: 12px; color: $text-sub; background: #e5e7eb; padding: 2px 8px; border-radius: 2px; }
  }

  .header-right {
    display: flex;
    gap: 6px;

    button {
      padding: 6px 12px;
      border: 1px solid $border;
      background: #fff;
      font-size: 12px;
      cursor: pointer;

      &:hover { background: $bg; }
      &.active { background: $text; color: #fff; border-color: $text; }
      &:disabled { opacity: 0.5; cursor: default; }
    }

    .save-btn {
      background: $text !important;
      color: #fff !important;
      border-color: $text !important;
      min-width: 60px;

      &:hover:not(:disabled) {
        background: #1f2937 !important;
        color: #fff !important;
      }

      &.unsaved { 
        background: #dc2626 !important; 
        border-color: #dc2626 !important; 
        
        &:hover:not(:disabled) {
          background: #b91c1c !important;
          color: #fff !important;
        }
      }
    }
  }
}

.table-wrapper {
  max-height: calc(100vh - 320px);
  min-height: 400px;
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #fafafa;
    font-weight: 500;
    color: $text-sub;
    border-bottom-color: $border;
    white-space: nowrap;

    &.r { text-align: right; }
    &.c { text-align: center; }
    &.highlight { background: #fef3c7; }
    &.col-no { width: 50px; }

    &.sortable {
      cursor: pointer;
      user-select: none;

      &:hover {
        background: #f0f0f0;
      }
    }

    .sort-icon {
      margin-left: 4px;
      font-size: 10px;
      color: $text;
    }
  }

  td {
    color: $text;

    &.r { text-align: right; font-variant-numeric: tabular-nums; }
    &.c { text-align: center; }
    &.highlight { background: #fffbeb; font-weight: 600; }
    &.col-no { 
      width: 50px; 
      color: $text-sub;
      font-size: 12px;
    }
  }

  tbody tr {
    cursor: pointer;
    &:hover { background: #e5e7eb; }
  }

  .name-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .name-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    flex-shrink: 0;
  }

  .name-text {
    font-weight: 500;
  }

  .badge {
    display: inline-block;
    margin-left: 4px;
    padding: 1px 5px;
    font-size: 10px;
    background: #ede9fe;
    color: #6d28d9;
    border-radius: 2px;
  }

  .muted { color: #999; }
  .warn { color: #dc2626; }
  .negative { color: #dc2626 !important; }
}

.list-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 10px 14px;
  background: #fafafa;
  border-top: 1px solid $border;

  .total-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;

    span:first-child { color: $text-sub; }
    span:last-child { font-weight: 600; color: $text; }

    &.primary {
      padding: 6px 12px;
      background: #fef3c7;

      span:first-child { color: #92400e; }
      span:last-child { color: #92400e; }
    }
  }
}
</style>
